import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FileText, Image as ImageIcon, FileSpreadsheet, File as FileIcon,
  Upload as UploadIcon, X, Plus, Trash2,
} from 'lucide-react';
import { tendersAPI, bidAgentsAPI, forgeAPI } from '../services/api';
import { logEvent } from '../utils/eventLog';
import { useBookmarks } from '../contexts/BookmarkContext';
import { useDisqualifications } from '../contexts/DisqualificationContext';
import './TenderDetailAugmented.css';

// Lucide icon for a given file type / extension. Used in both the
// upload modal queue and the existing-documents list.
const fileTypeIcon = (filenameOrType, size = 16) => {
  const v = (filenameOrType || '').toLowerCase();
  if (v.endsWith('.pdf') || v === 'pdf') return <FileText size={size} />;
  if (v.match(/\.(png|jpe?g|gif|webp|tiff?|bmp)$/) || v === 'image') return <ImageIcon size={size} />;
  if (v.match(/\.(csv|xlsx?|tsv|ods)$/) || v === 'spreadsheet') return <FileSpreadsheet size={size} />;
  if (v.match(/\.(docx?|pptx?|odt|rtf)$/) || v === 'document') return <FileText size={size} />;
  return <FileIcon size={size} />;
};

const formatBytes = (b) => {
  if (!b && b !== 0) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const TenderDetailAugmented = () => {
  const { id: encodedId } = useParams();
  const id = decodeURIComponent(encodedId);
  const navigate = useNavigate();
  const [tender, setTender] = useState(null);
  // The backend may resolve the URL id to a different canonical id (e.g. "onref<uuid>"
  // vs bare "<uuid>"). Use resolvedId for all bookmark/DQ operations so they match
  // what addBookmark stores.
  const resolvedId = tender?.tender_id || id;
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  // Wizard-style upload queue: files accumulate here as the user picks
  // them, then a single Upload button submits the whole batch. Validation
  // errors land alongside in queueErrors so they're surfaced inline rather
  // than as a blocking alert.
  const [queuedFiles, setQueuedFiles] = useState([]);
  const [queueErrors, setQueueErrors] = useState([]);
  const uploadInputRef = useRef(null);
  const [busyDeleteUrl, setBusyDeleteUrl] = useState(null);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const { isBookmarked, toggleBookmark, addBookmark, bookmarks, updateBookmarkFields } = useBookmarks();
  const { isDisqualified, disqualify, undisqualify } = useDisqualifications();

  // Sprint 04-30 P1: confirmation modal for the detail-page disqualify button.
  // Captures an optional reason note before posting to the team-shared list.
  const [dqModalOpen, setDqModalOpen] = useState(false);
  const [dqReason, setDqReason] = useState('');
  const [dqBusy, setDqBusy] = useState(false);
  const submitDisqualify = async () => {
    setDqBusy(true);
    try {
      await disqualify({
        tender_id: resolvedId,
        title: tender?.title,
        organization: tender?.organization,
        closing_date: tender?.closing_date,
        reason: dqReason.trim() || null,
      });
      setDqModalOpen(false);
      setDqReason('');
    } catch (e) {
      console.error('disqualify failed', e);
    } finally {
      setDqBusy(false);
    }
  };

  // Quick summary state
  const [quickSummary, setQuickSummary] = useState(null);
  const [quickSummaryLoading, setQuickSummaryLoading] = useState(false);

  // Notes state
  const [notes, setNotes] = useState('');
  const notesTimeoutRef = useRef(null);

  useEffect(() => {
    fetchTenderDetails();
    logEvent('tender.viewed', { tender_id: id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Load saved notes from bookmark
  useEffect(() => {
    const bookmark = bookmarks.find(b => b.id === resolvedId || b.tender_id === resolvedId || b.id === id || b.tender_id === id);
    if (bookmark) {
      if (bookmark.notes) setNotes(bookmark.notes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookmarks, id]);

  const fetchTenderDetails = async () => {
    setLoading(true);
    try {
      const response = await tendersAPI.getTender(id);
      setTender(response);
    } catch (error) {
      console.error('Error fetching tender:', error);
      setTender(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTender = async () => {
    if (tender) {
      await addBookmark(tender);
    }
  };

  const handleNotesChange = (value) => {
    setNotes(value);
    if (notesTimeoutRef.current) clearTimeout(notesTimeoutRef.current);
    notesTimeoutRef.current = setTimeout(() => {
      updateBookmarkFields(id, { notes: value });
    }, 800);
  };

  const handleQuickSummary = async () => {
    setQuickSummaryLoading(true);
    try {
      const result = await bidAgentsAPI.quickSummary(id);
      setQuickSummary(result);
    } catch (error) {
      console.error('Quick summary failed:', error);
      setQuickSummary({ summary: 'Failed to generate summary. Try again or run full analysis.', confidence: 'low' });
    } finally {
      setQuickSummaryLoading(false);
    }
  };

  const formatCurrency = (value) => {
    const amount = parseFloat(value || 0);
    if (amount === 0) return 'Not disclosed';
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M CAD`;
    }
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      const d = new Date(date);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return 'N/A';
    }
  };

  const getPlatformName = (tender) => {
    if (tender.platform_classification?.platform_name) {
      return tender.platform_classification.platform_name;
    }
    const url = tender.external_platform_url || tender.notice_url;
    if (!url) return 'CanadaBuys';
    const urlLower = url.toLowerCase();

    if (urlLower.includes('bonfirehub.com') || urlLower.includes('gobonfire.com')) return 'Bonfire';
    if (urlLower.includes('merx.com')) return 'MERX';
    if (urlLower.includes('buyandsell.gc.ca') || urlLower.includes('canadabuys.canada.ca')) return 'CanadaBuys';
    if (urlLower.includes('ariba.com') || urlLower.includes('sap.com')) return 'SAP Ariba';
    if (urlLower.includes('purchasingconnection.ca') || urlLower.includes('alberta.ca')) return 'Alberta Purchasing';
    if (urlLower.includes('bidsandtenders.ca')) return 'Bids & Tenders';
    return 'CanadaBuys';
  };

  const getDisplayStatus = (tender) => {
    const closingDate = tender.closing_date;
    const esStatus = (tender.status || '').toLowerCase();

    if (closingDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const closeDate = new Date(closingDate);
      closeDate.setHours(0, 0, 0, 0);

      if (closeDate < today) {
        if (esStatus === 'active') return 'Closing Date Passed';
        return 'Closed';
      }
    }

    return tender.status || 'Open';
  };

  const getStatusColor = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower === 'closing date passed') return 'status-closed';
    if (statusLower.includes('active') || statusLower.includes('open')) return 'status-open';
    if (statusLower.includes('closed') || statusLower.includes('expired')) return 'status-closed';
    if (statusLower.includes('awarded')) return 'status-awarded';
    return 'status-default';
  };

  const SUPPORTED_FILE_TYPES = {
    pdf: { accept: 'application/pdf', extensions: ['.pdf'], icon: '📄', maxSize: 50 },
    image: { accept: 'image/jpeg,image/png,image/tiff', extensions: ['.jpg', '.jpeg', '.png', '.tiff'], icon: '🖼️', maxSize: 25 },
    spreadsheet: { accept: 'text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', extensions: ['.csv', '.xlsx', '.xls'], icon: '📊', maxSize: 10 }
  };

  const detectFileType = (filename) => {
    const ext = '.' + filename.split('.').pop().toLowerCase();
    for (const [type, config] of Object.entries(SUPPORTED_FILE_TYPES)) {
      if (config.extensions.includes(ext)) {
        return { type, config };
      }
    }
    return null;
  };

  const getFileIcon = (fileType) => {
    const icons = { pdf: '📄', image: '🖼️', spreadsheet: '📊', csv: '📊', xlsx: '📊' };
    return icons[fileType] || '📄';
  };

  // ──────────────── Upload wizard helpers ────────────────

  // Add picker selections to the queue, validating per-file. Dedupes by
  // name+size so re-picking the same files doesn't double them up.
  const addFilesToQueue = (selected) => {
    const fileList = Array.from(selected || []);
    if (fileList.length === 0) return;

    setQueuedFiles((existing) => {
      const seen = new Set(existing.map((f) => `${f.name}|${f.size}`));
      const accepted = [...existing];
      const errs = [];
      for (const file of fileList) {
        const key = `${file.name}|${file.size}`;
        if (seen.has(key)) continue; // dedupe
        const info = detectFileType(file.name);
        if (!info) {
          errs.push(`${file.name}: unsupported file type`);
          continue;
        }
        const sizeMB = file.size / (1024 * 1024);
        if (sizeMB > info.config.maxSize) {
          errs.push(`${file.name}: exceeds ${info.config.maxSize}MB limit`);
          continue;
        }
        accepted.push(file);
        seen.add(key);
      }
      if (errs.length > 0) setQueueErrors((prev) => [...prev, ...errs]);
      return accepted;
    });
  };

  const removeFromQueue = (idx) => {
    setQueuedFiles((q) => q.filter((_, i) => i !== idx));
  };

  const closeUploadModal = () => {
    if (uploadingPdf) return;
    setShowUploadModal(false);
    setQueuedFiles([]);
    setQueueErrors([]);
  };

  const submitQueuedUploads = async () => {
    if (queuedFiles.length === 0 || uploadingPdf) return;
    setUploadingPdf(true);
    try {
      const tenderId = tender?._id || id;
      const platformName = getPlatformName(tender);

      const formData = new FormData();
      for (const f of queuedFiles) formData.append('files', f);
      formData.append('tender_id', tenderId);
      formData.append('platform_name', platformName);

      const response = await fetch(`/api/documents/upload`, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      if (response.status === 200 || response.status === 207) {
        const failed = (data.failed || []).map((r) => `${r.filename}: ${r.error}`);
        if (failed.length === 0 && queueErrors.length === 0) {
          // success — silent close
        } else {
          alert(
            `Uploaded ${data.succeeded_count} of ${queuedFiles.length} file${queuedFiles.length !== 1 ? 's' : ''}.\n\n` +
            `Failed:\n${[...queueErrors, ...failed].join('\n')}`,
          );
        }
        setShowUploadModal(false);
        setQueuedFiles([]);
        setQueueErrors([]);
        await fetchTenderDetails();
      } else {
        // FastAPI validation errors come back as detail:[{msg,loc,...}] — coerce to
        // a string so it never renders as an object (React #31) / "[object Object]".
        const d = data && data.detail;
        const msg = Array.isArray(d)
          ? d.map((x) => (x && x.msg) || (typeof x === 'string' ? x : JSON.stringify(x))).join('; ')
          : (typeof d === 'string' ? d : (data && data.error) || 'Upload failed');
        throw new Error(msg);
      }
    } catch (error) {
      console.error('Error uploading file(s):', error);
      alert(`Failed to upload: ${error.message}`);
    } finally {
      setUploadingPdf(false);
    }
  };

  // Delete an existing discovered document from the tender. Used by the
  // X button on each row in the Documents panel.
  const handleDeleteDoc = async (doc) => {
    const title = doc.title || doc.filename || 'this document';
    if (!window.confirm(`Remove "${title}" from this tender?\n\nThe S3 file is kept; only the listing is removed.`)) return;
    setBusyDeleteUrl(doc.url);
    try {
      const res = await fetch('/api/documents/discovered', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tender_id: id,
          url: doc.url || null,
          title: doc.title || doc.filename || null,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Delete failed');
      }
      await fetchTenderDetails();
    } catch (e) {
      console.error('Delete document failed:', e);
      alert(`Failed to remove document: ${e.message}`);
    } finally {
      setBusyDeleteUrl(null);
    }
  };

  const handleDocumentView = async (doc) => {
    const fileType = doc.file_type || 'pdf';

    if (fileType === 'image' || fileType === 'spreadsheet') {
      try {
        const response = await fetch(`/api/pdfs/view?url=${encodeURIComponent(doc.url)}`);
        if (response.ok) {
          const data = await response.json();
          window.open(data.url, '_blank');
        }
      } catch (error) {
        alert('Failed to load document.');
      }
      return;
    }

    handlePdfView(doc.url, doc.title);
  };

  const handlePdfView = async (pdfUrl, pdfTitle) => {
    try {
      const response = await fetch(`/api/pdfs/view?url=${encodeURIComponent(pdfUrl)}`);

      if (response.ok) {
        const data = await response.json();

        if (data.type === 'external') {
          window.open(data.url, '_blank');
          return;
        }

        setLoadingPdf(true);
        setShowPdfViewer(true);
        setCurrentPdfUrl(data.url);
        setLoadingPdf(false);
      }
    } catch (error) {
      console.error('Error loading PDF:', error);
      alert('Failed to load PDF.');
    }
  };

  if (loading) {
    return (
      <div className="tender-detail-augmented">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading tender details...</p>
        </div>
      </div>
    );
  }

  if (!tender) {
    return (
      <div className="tender-detail-augmented">
        <div className="error-container">
          <h2>Tender not found</h2>
          <button onClick={() => navigate('/dashboard')} className="btn-primary">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const bi = tender.business_intelligence || {};
  const org = typeof tender.organization === 'object' ? tender.organization : {};
  const orgName = typeof tender.organization === 'string'
    ? tender.organization
    : (org.name || org.contracting_entity || 'N/A');
  const platformName = getPlatformName(tender);
  const externalPlatformUrl = tender.external_platform_url || tender.notice_url;
  const displayStatus = getDisplayStatus(tender);
  const statusClass = getStatusColor(displayStatus);

  const docCount = (tender.discovered_content?.documents?.length || 0) +
                   (tender.discovered_pdfs?.length || 0) +
                   (tender.documents?.length || 0);

  const aiDescription = tender['aiDescription-descriptionIA-eng'] ||
                        bi.ai_description_eng ||
                        tender.description ||
                        null;

  const saved = isBookmarked(resolvedId);

  return (
    <div className="tender-detail-augmented">
      <div className="container">
        {/* Back Button */}
        <button onClick={() => navigate(-1)} className="back-button">
          ← Back to Results
        </button>

        {/* Header Card */}
        <div className="header-card-new">
          <div className="header-top-row">
            <div className="header-title-section">
              <h1 className="tender-title-new">{tender.title}</h1>
              <span className={`status-badge-new ${statusClass}`}>
                {displayStatus}
              </span>
            </div>
            <div className="header-actions-new">
              {externalPlatformUrl && (
                <a
                  href={externalPlatformUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary"
                >
                  View on {platformName}
                </a>
              )}
              <button
                onClick={saved ? () => toggleBookmark({ id: resolvedId }) : handleSaveTender}
                className={`btn-save ${saved ? 'saved' : ''}`}
              >
                {saved ? '★ Saved' : '☆ Save'}
              </button>
              <button
                onClick={async () => {
                  if (isDisqualified(resolvedId)) {
                    try { await undisqualify(resolvedId); }
                    catch (e) { console.error('restore failed', e); }
                    return;
                  }
                  // Open the reason-note modal instead of disqualifying immediately
                  setDqReason('');
                  setDqModalOpen(true);
                }}
                className={`btn-secondary ${isDisqualified(resolvedId) ? 'disqualified' : ''}`}
                title={isDisqualified(resolvedId) ? 'Restore from disqualified' : 'Disqualify (hide from team search)'}
              >
                {isDisqualified(resolvedId) ? '↺ Restore' : '⊘ Disqualify'}
              </button>
            </div>
          </div>

          {dqModalOpen && (
            <div
              className="dq-modal-overlay"
              onClick={() => !dqBusy && setDqModalOpen(false)}
              role="dialog"
              aria-modal="true"
            >
              <div className="dq-modal" onClick={(e) => e.stopPropagation()}>
                <h3 className="dq-modal-title">Disqualify this tender?</h3>
                <p className="dq-modal-subtitle">
                  This will hide the tender from the team's search results.
                  Add an optional note so teammates know why.
                </p>
                <textarea
                  className="dq-modal-textarea"
                  placeholder="Reason (optional) — e.g. wrong region, lacks required certification, services contract…"
                  value={dqReason}
                  onChange={(e) => setDqReason(e.target.value)}
                  rows={3}
                  autoFocus
                  disabled={dqBusy}
                />
                <div className="dq-modal-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setDqModalOpen(false)}
                    disabled={dqBusy}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn-secondary disqualified"
                    onClick={submitDisqualify}
                    disabled={dqBusy}
                  >
                    {dqBusy ? 'Disqualifying…' : 'Disqualify'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 4-Pill Meta Row */}
          <div className="meta-pills">
            <div className="meta-pill">
              <span className="pill-label">Est. Amount</span>
              <span className="pill-value">{formatCurrency(tender.value?.amount)}</span>
            </div>
            <div className="meta-pill">
              <span className="pill-label">Closing Date</span>
              <span className="pill-value">{formatDate(tender.closing_date)}</span>
            </div>
            <div className="meta-pill">
              <span className="pill-label">Buyer</span>
              <span className="pill-value">{orgName}</span>
            </div>
            <div className="meta-pill">
              <span className="pill-label">Category</span>
              <span className="pill-value">
                {bi.master_category || tender.notice_type || 'General'}
              </span>
            </div>
          </div>
        </div>

        {/* 3-Column Grid Layout */}
        <div className="tender-grid-new">
          {/* Left Column - Documents */}
          <div className="documents-panel">
            <div className="panel-header">
              <h3>Documents</h3>
              <span className="doc-count">{docCount} found</span>
            </div>
            <div className="panel-actions">
              {docCount > 1 && (
                <button className="btn-small">Download All</button>
              )}
              <button className="btn-small primary" onClick={() => setShowUploadModal(true)}>
                Upload
              </button>
            </div>

            {/* Document List */}
            <div className="doc-list">
              {tender.discovered_content?.documents?.map((doc, idx) => (
                <div key={`doc-${idx}`} className="doc-item">
                  <button onClick={() => handleDocumentView(doc)} className="doc-link">
                    <span className="doc-link-icon">{fileTypeIcon(doc.file_type || doc.title, 14)}</span>
                    <span className="doc-link-title">{doc.title}</span>
                  </button>
                  {doc.file_type && (
                    <span className="doc-type-badge">{doc.file_type.toUpperCase()}</span>
                  )}
                  <button
                    className="doc-delete"
                    onClick={(e) => { e.stopPropagation(); handleDeleteDoc(doc); }}
                    disabled={busyDeleteUrl === doc.url}
                    title="Remove document from this tender"
                  >
                    {busyDeleteUrl === doc.url
                      ? <span className="loading-spinner-small" />
                      : <Trash2 size={13} />}
                  </button>
                </div>
              ))}

              {tender.discovered_pdfs?.map((pdf, idx) => (
                <div key={`pdf-${idx}`} className="doc-item">
                  <button onClick={() => handlePdfView(pdf.url, pdf.title)} className="doc-link">
                    <span className="doc-link-icon"><FileText size={14} /></span>
                    <span className="doc-link-title">{pdf.title}</span>
                  </button>
                </div>
              ))}

              {tender.documents?.map((doc, idx) => {
                try {
                  let docUrl = doc.url;
                  let docTitle = doc.filename;

                  if (typeof docUrl === 'string' && (docUrl.startsWith('[') || docUrl.startsWith('{'))) {
                    const parsed = JSON.parse(docUrl);
                    if (Array.isArray(parsed)) {
                      return parsed.map((pdfData, pdfIdx) => (
                        <div key={`${idx}-${pdfIdx}`} className="doc-item">
                          <button
                            onClick={() => handlePdfView(pdfData.url, pdfData.text || doc.filename)}
                            className="doc-link"
                          >
                            <span className="doc-link-icon"><FileText size={14} /></span>
                            <span className="doc-link-title">{pdfData.text || doc.filename}</span>
                          </button>
                        </div>
                      ));
                    }
                    docUrl = parsed.url;
                    docTitle = parsed.text || doc.filename;
                  }

                  return (
                    <div key={idx} className="doc-item">
                      <button onClick={() => handlePdfView(docUrl, docTitle)} className="doc-link">
                        <span className="doc-link-icon"><FileText size={14} /></span>
                        <span className="doc-link-title">{docTitle}</span>
                      </button>
                    </div>
                  );
                } catch {
                  return null;
                }
              })}

              {docCount === 0 && (
                <div className="doc-empty">
                  <p>No documents found.</p>
                  {externalPlatformUrl && (
                    <a href={externalPlatformUrl} target="_blank" rel="noopener noreferrer">
                      Visit {platformName} →
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Center Column - Tender Info + Analysis */}
          <div className="tab-content-panel">
            {/* Description */}
            {aiDescription && (
              <div className="tender-info-section">
                <h3 className="section-title">Description</h3>
                <p className="ai-description-text">{aiDescription}</p>
              </div>
            )}

            {/* Quick AI Summary */}
            <div className="tender-info-section">
              {quickSummary ? (
                <>
                  <h3 className="section-title">AI Summary</h3>
                  <p className="ai-description-text">{quickSummary.summary}</p>
                  {quickSummary.products_identified?.length > 0 && (
                    <div className="quick-summary-details">
                      <span className="quick-summary-label">Products:</span>
                      <ul className="quick-summary-list">
                        {quickSummary.products_identified.map((p, i) => (
                          <li key={i}>{p}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {quickSummary.key_requirements?.length > 0 && (
                    <div className="quick-summary-details">
                      <span className="quick-summary-label">Key Requirements:</span>
                      <ul className="quick-summary-list">
                        {quickSummary.key_requirements.map((r, i) => (
                          <li key={i}>{r}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              ) : (
                <button
                  className="btn-quick-summary"
                  onClick={handleQuickSummary}
                  disabled={quickSummaryLoading}
                >
                  {quickSummaryLoading ? 'Generating summary...' : 'Get AI Summary'}
                </button>
              )}
            </div>

            {/* Tender Description (raw, if different from AI description) */}
            {tender.description && tender.description !== aiDescription && !quickSummary && (
              <div className="tender-info-section">
                <h3 className="section-title">Raw Description</h3>
                <p className="description-text">{tender.description}</p>
              </div>
            )}

            {/* Save CTA (not saved yet) */}
            {!saved && (
              <div className="analysis-cta">
                <div className="cta-icon">★</div>
                <h3>Save to run AI analysis</h3>
                <p>Save this tender to your bookmarks, then run the AI analysis pipeline to get qualification, historicals, sourcing, and bid prep data.</p>
                <button className="btn-cta" onClick={handleSaveTender}>
                  ☆ Save Tender
                </button>
              </div>
            )}

            {/* Notes + Analysis CTA (saved) */}
            {saved && (
              <>
                <div className="workspace-field">
                  <label className="workspace-label">Notes</label>
                  <textarea
                    className="workspace-textarea"
                    placeholder="Add notes, observations, or requirements for this tender..."
                    value={notes}
                    onChange={(e) => handleNotesChange(e.target.value)}
                    rows={4}
                  />
                </div>

                <div className="analysis-cta">
                  {bookmarks.find(b => b.id === id || b.tender_id === id)?.analysisStatus === 'complete' ? (
                    <>
                      <div className="cta-icon">&#10003;</div>
                      <h3>Analysis Complete</h3>
                      <p>View full analysis results, or build the bid in FORGE (spec sheet, sourcing, quotes, pricing).</p>
                      <button
                        className="btn-cta"
                        onClick={() => navigate(`/responses/${encodeURIComponent(id)}`)}
                      >
                        View Analysis Results &rarr;
                      </button>
                      {/* Discovery → FORGE: only on analysed tenders */}
                      <button
                        className="btn-cta btn-cta-forge"
                        onClick={() => { forgeAPI.enterForge(id, '').catch(() => {}); navigate(`/forge?tender=${encodeURIComponent(id)}`); }}
                      >
                        Build bid in FORGE &rarr;
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="cta-icon">&#9889;</div>
                      <h3>Run AI Analysis</h3>
                      <p>Run the analysis pipeline to get qualification assessment, historical comparisons, sourcing recommendations, and bid preparation data.</p>
                      <button
                        className="btn-cta"
                        onClick={() => navigate(`/responses/${encodeURIComponent(id)}`)}
                      >
                        Run AI Analysis &rarr;
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Right Column - Key Details */}
          <div className="details-panel">
            <div className="kv-section">
              <h4>Key Details</h4>
              <div className="kv-item">
                <span className="kv-label">Solicitation #</span>
                <span className="kv-value">{tender.solicitation_number || tender.reference_number || 'N/A'}</span>
              </div>
              <div className="kv-item">
                <span className="kv-label">Region</span>
                <span className="kv-value">
                  {tender.regions_of_opportunity?.[0] || bi.delivery_regions?.[0] || 'Canada'}
                </span>
              </div>
              <div className="kv-item">
                <span className="kv-label">Language</span>
                <span className="kv-value">{tender.language || 'English'}</span>
              </div>
              <div className="kv-item">
                <span className="kv-label">Method</span>
                <span className="kv-value">{tender.procurement_method || 'Competitive'}</span>
              </div>
              {(tender.gsin_codes?.length > 0 || tender.gsin_description) && (
                <div className="kv-item">
                  <span className="kv-label">GSIN</span>
                  <span className="kv-value">
                    {tender.gsin_codes?.join(', ') || 'N/A'}
                    {tender.gsin_description && (
                      <span className="kv-sub">{tender.gsin_description}</span>
                    )}
                  </span>
                </div>
              )}
              {tender.unspsc_codes?.length > 0 && (
                <div className="kv-item">
                  <span className="kv-label">UNSPSC</span>
                  <span className="kv-value">
                    {tender.unspsc_codes.join(', ')}
                    {tender.unspsc_descriptions?.length > 0 && (
                      <span className="kv-sub">{tender.unspsc_descriptions[0]}</span>
                    )}
                  </span>
                </div>
              )}
            </div>

            <div className="kv-section">
              <h4>Contact</h4>
              <div className="kv-item">
                <span className="kv-label">Authority</span>
                <span className="kv-value">{orgName}</span>
              </div>
              {org.contact_name && (
                <div className="kv-item">
                  <span className="kv-label">Name</span>
                  <span className="kv-value">{org.contact_name}</span>
                </div>
              )}
              {org.contact_email && (
                <div className="kv-item">
                  <span className="kv-label">Email</span>
                  <span className="kv-value">
                    <a href={`mailto:${org.contact_email}`}>{org.contact_email}</a>
                  </span>
                </div>
              )}
              {org.contact_phone && (
                <div className="kv-item">
                  <span className="kv-label">Phone</span>
                  <span className="kv-value">{org.contact_phone}</span>
                </div>
              )}
            </div>

            {tender.trade_agreements?.length > 0 && (
              <div className="kv-section">
                <h4>Trade Agreements</h4>
                <div className="tag-list-new">
                  {tender.trade_agreements.map((agreement, i) => (
                    <span key={i} className="tag-new">{agreement}</span>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={closeUploadModal}>
          <div className="modal-content upload-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Upload documents</h3>
              <button className="modal-close" onClick={closeUploadModal}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <p className="modal-description">
                Add one or more files to this tender. Documents from{' '}
                <strong>{platformName}</strong> will be searchable + processed by AI.
              </p>

              {/* Supported types row — lucide icons */}
              <div className="supported-types modern">
                <div className="file-type-item">
                  <FileText size={16} className="type-icon-svg" />
                  <span className="type-name">PDFs</span>
                  <span className="type-limit">50 MB</span>
                </div>
                <div className="file-type-item">
                  <ImageIcon size={16} className="type-icon-svg" />
                  <span className="type-name">Images</span>
                  <span className="type-limit">25 MB</span>
                </div>
                <div className="file-type-item">
                  <FileSpreadsheet size={16} className="type-icon-svg" />
                  <span className="type-name">Spreadsheets</span>
                  <span className="type-limit">10 MB</span>
                </div>
                <div className="file-type-item">
                  <FileIcon size={16} className="type-icon-svg" />
                  <span className="type-name">Word / PPT</span>
                  <span className="type-limit">25 MB</span>
                </div>
              </div>

              {/* Hidden input — driven by the drop-zone and "Add more" button */}
              <input
                ref={uploadInputRef}
                type="file"
                multiple
                accept="application/pdf,image/jpeg,image/png,image/tiff,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                disabled={uploadingPdf}
                style={{ display: 'none' }}
                onChange={(e) => {
                  addFilesToQueue(e.target.files);
                  e.target.value = '';
                }}
              />

              {queuedFiles.length === 0 ? (
                <button
                  type="button"
                  className="upload-zone modern"
                  onClick={() => uploadInputRef.current?.click()}
                  disabled={uploadingPdf}
                >
                  <UploadIcon size={28} className="upload-zone-icon" />
                  <span className="upload-zone-title">Click to select files</span>
                  <span className="upload-zone-hint">
                    Hold <kbd>Cmd</kbd> / <kbd>Ctrl</kbd> to pick more than one
                  </span>
                </button>
              ) : (
                <>
                  <div className="upload-queue">
                    <div className="upload-queue-head">
                      <span>{queuedFiles.length} file{queuedFiles.length !== 1 ? 's' : ''} queued</span>
                      <button
                        type="button"
                        className="upload-queue-clear"
                        onClick={() => { setQueuedFiles([]); setQueueErrors([]); }}
                        disabled={uploadingPdf}
                      >
                        Clear all
                      </button>
                    </div>
                    {queuedFiles.map((f, i) => (
                      <div key={`${f.name}-${i}`} className="upload-queue-row">
                        <span className="upload-queue-icon">{fileTypeIcon(f.name, 16)}</span>
                        <span className="upload-queue-name" title={f.name}>{f.name}</span>
                        <span className="upload-queue-size">{formatBytes(f.size)}</span>
                        <button
                          type="button"
                          className="upload-queue-remove"
                          onClick={() => removeFromQueue(i)}
                          disabled={uploadingPdf}
                          title="Remove from queue"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                  {queueErrors.length > 0 && (
                    <div className="upload-queue-errors">
                      {queueErrors.map((e, i) => <div key={i}>⚠ {e}</div>)}
                    </div>
                  )}
                </>
              )}

              <div className="upload-modal-footer">
                <button
                  type="button"
                  className="btn-secondary upload-add-more"
                  onClick={() => uploadInputRef.current?.click()}
                  disabled={uploadingPdf}
                >
                  <Plus size={14} /> Add more
                </button>
                <div style={{ flex: 1 }} />
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeUploadModal}
                  disabled={uploadingPdf}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={submitQueuedUploads}
                  disabled={queuedFiles.length === 0 || uploadingPdf}
                >
                  {uploadingPdf ? (
                    <>
                      <span className="loading-spinner-small" /> Uploading…
                    </>
                  ) : (
                    <>
                      <UploadIcon size={14} /> Upload {queuedFiles.length || ''}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PDF Viewer Modal */}
      {showPdfViewer && (
        <div className="modal-overlay pdf-viewer-overlay" onClick={() => setShowPdfViewer(false)}>
          <div className="modal-content pdf-viewer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📄 PDF Viewer</h3>
              <button className="modal-close" onClick={() => setShowPdfViewer(false)}>×</button>
            </div>
            <div className="pdf-viewer-container">
              {loadingPdf ? (
                <div className="pdf-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading PDF...</p>
                </div>
              ) : currentPdfUrl ? (
                <iframe
                  src={currentPdfUrl}
                  title="PDF Viewer"
                  className="pdf-iframe"
                />
              ) : (
                <div className="pdf-error">
                  <p>Failed to load PDF. Please try again.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenderDetailAugmented;
